/**
 * PublicSongPortal — read-only page the congregation opens to see this
 * week's worship set. Uses PUBLIC endpoints (no auth) so anyone with the
 * link can view. Reachable via three shapes:
 *   • /portal/service/:serviceId  → direct
 *   • /portal/:slug/:date         → resolved via /portal-lookup
 *   • /portal                     → boots into last-viewed service
 *
 * PWA (Portal PWA — P2): once installed, the app opens straight to the
 * last-viewed set. Users tap "Save for offline" to pre-cache the whole
 * service so the drive to church still works with no signal.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Copy, Music, Calendar, BookOpen, Play, ArrowLeft,
  Download, DownloadCloud, Check, Smartphone, WifiOff, X,
} from 'lucide-react';

import { usePortalPWA, readLastPortal } from './usePortalPWA';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicSongPortal() {
  const params = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showIosHelp, setShowIosHelp] = useState(false);

  const [resolvedServiceId, setResolvedServiceId] = useState(null);

  // Cold-launch boot into last-viewed set when opened at bare /portal
  useEffect(() => {
    if (!params.serviceId && !params.slug) {
      const last = readLastPortal();
      if (last.url && last.url !== window.location.pathname) {
        navigate(last.url, { replace: true });
      }
    }
  }, [params.serviceId, params.slug, navigate]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        let serviceId = params.serviceId;
        if (!serviceId && params.slug) {
          const r = await axios.get(
            `${API}/api/public/worship/portal-lookup`,
            { params: { slug: params.slug, date: params.date } },
          );
          serviceId = r.data?.service_id;
        }
        if (!serviceId) throw new Error('service id missing');
        const r2 = await axios.get(`${API}/api/public/worship/services/${serviceId}`);
        setService(r2.data);
        setResolvedServiceId(serviceId);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || 'unable to load service');
      } finally {
        setLoading(false);
      }
    })();
  }, [params.serviceId, params.slug, params.date]);

  // Swap the document's manifest to the per-church portal manifest so
  // the installed app is branded correctly. Restores on unmount so
  // navigating back to the admin app keeps the EventFlow manifest.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]#manifest-link');
    if (!link) return;
    const prev = link.href;
    const qs = new URLSearchParams();
    if (resolvedServiceId) qs.set('service_id', resolvedServiceId);
    if (params.slug) qs.set('slug', params.slug);
    const href = `${API}/api/public/worship/manifest.json${qs.toString() ? `?${qs}` : ''}`;
    link.href = href;
    // theme-color for the address bar on Android
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = themeMeta?.content;
    if (themeMeta) themeMeta.content = '#4c1d95';
    return () => {
      link.href = prev;
      if (themeMeta && prevTheme) themeMeta.content = prevTheme;
    };
  }, [resolvedServiceId, params.slug]);

  const pwa = usePortalPWA({ serviceId: resolvedServiceId });

  const copyLink = () => {
    try { navigator.clipboard.writeText(window.location.href); toast.success('Link copied'); }
    catch { toast.error('Copy failed'); }
  };

  const handleInstall = async () => {
    if (pwa.isIOS && !pwa.isInstalled) {
      setShowIosHelp(true);
      return;
    }
    const ok = await pwa.install();
    if (ok) toast.success('App installed — check your home screen');
  };

  const handleSaveOffline = async () => {
    if (!resolvedServiceId) return;
    const t = toast.loading('Saving set for offline…');
    const ok = await pwa.saveForOffline(resolvedServiceId);
    toast.dismiss(t);
    if (ok) toast.success('Saved — this set works offline now');
    else toast.error('Could not save for offline');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-400 flex items-center justify-center" data-testid="portal-loading">
        Loading…
      </div>
    );
  }
  if (error || !service) {
    // Distinguish "no context yet" (bare /portal cold launch — friendly
    // welcome) from an actual error (bad slug / not published).
    const isBareEntry = !params.serviceId && !params.slug;
    if (isBareEntry) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950 to-slate-950 text-slate-100 flex flex-col items-center justify-center gap-4 p-6" data-testid="portal-welcome">
          <div className="text-[10px] uppercase tracking-widest text-violet-300">Worship Portal</div>
          <h1 className="text-2xl font-bold">Welcome</h1>
          <p className="text-sm text-slate-400 max-w-sm text-center">
            To see this week&apos;s worship set, open the shareable link your church
            worship team sent you. Once you visit it, this app will remember and
            open straight to it next time.
          </p>
          <p className="text-[11px] text-slate-500 max-w-xs text-center">
            Ask your worship leader for the portal link — it usually looks like
            <span className="text-slate-300"> /portal/service/…</span> or
            <span className="text-slate-300"> /portal/your-church/date</span>.
          </p>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center gap-3 p-6" data-testid="portal-error">
        <p className="text-lg font-semibold text-rose-300">Not available</p>
        <p className="text-sm text-slate-400 max-w-md text-center">
          {error || 'This service isn\'t published for public viewing yet. Ask your church admin to publish it.'}
        </p>
        <Link to="/" className="text-xs text-violet-300 hover:underline"><ArrowLeft className="mr-1 inline-block h-3 w-3" /> Go home</Link>
      </div>
    );
  }

  const songBlocks = (service.blocks || []).filter((b) => b.song);
  const outline = service.blocks || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950 to-slate-950 text-slate-100" data-testid="portal-page">
      {/* Offline banner */}
      {pwa.isOffline && (
        <div
          data-testid="portal-offline-banner"
          className="bg-amber-500/20 border-b border-amber-500/40 text-amber-100 text-xs py-1.5 px-4 flex items-center justify-center gap-2"
        >
          <WifiOff className="h-3 w-3" />
          You&apos;re offline — showing your saved copy of this set.
        </div>
      )}

      {/* Header */}
      <div className="border-b border-white/5 backdrop-blur-sm sticky top-0 z-10 bg-slate-950/70">
        <div className="mx-auto max-w-4xl px-4 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-violet-300">Worship Portal</div>
            <h1 className="text-xl font-bold truncate" data-testid="portal-service-name">{service.name}</h1>
            <div className="text-[11px] text-slate-400 flex flex-wrap gap-3 mt-0.5">
              {service.scheduled_at && (
                <span><Calendar className="mr-1 inline-block h-3 w-3" /> {new Date(service.scheduled_at).toLocaleString([], { dateStyle: 'full', timeStyle: 'short' })}</span>
              )}
              {service.speaker && <span>Speaker: <span className="text-slate-200">{service.speaker}</span></span>}
              {service.theme && <span>Theme: <span className="text-slate-200">{service.theme}</span></span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {pwa.canInstall && !pwa.isInstalled && (
              <button
                type="button"
                data-testid="portal-install-btn"
                onClick={handleInstall}
                className="rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-200 hover:bg-emerald-500/20 flex items-center gap-1"
              >
                <Smartphone className="h-3 w-3" /> Install App
              </button>
            )}
            <button
              type="button"
              data-testid="portal-copy-link"
              onClick={copyLink}
              className="rounded border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-xs text-violet-200 hover:bg-violet-500/20"
            >
              <Copy className="mr-1 inline-block h-3 w-3" /> Share
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-6 space-y-6">

        {/* Save for offline strip */}
        <section
          data-testid="portal-offline-strip"
          className="rounded-lg border border-white/10 bg-gradient-to-r from-violet-500/10 to-indigo-500/10 p-4 flex flex-wrap items-center justify-between gap-3"
        >
          <div>
            <div className="text-sm font-semibold text-slate-100 flex items-center gap-2">
              <DownloadCloud className="h-4 w-4 text-violet-300" />
              Practice on the drive to church
            </div>
            <p className="text-[11px] text-slate-400 mt-1 max-w-lg">
              Save this set to your phone so lyrics, chords and lead sheets all work even
              without signal. {pwa.isInstalled ? 'The app is installed — you\'re all set.'
                : 'Tap Install App above to keep it one tap away.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {pwa.isSavedOffline ? (
              <span
                data-testid="portal-saved-badge"
                className="rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-200 flex items-center gap-1"
              >
                <Check className="h-3 w-3" /> Saved for offline
              </span>
            ) : (
              <button
                type="button"
                data-testid="portal-save-offline-btn"
                onClick={handleSaveOffline}
                disabled={pwa.saveState === 'saving'}
                className="rounded border border-violet-500/50 bg-violet-500/20 px-3 py-1.5 text-xs text-violet-100 hover:bg-violet-500/30 disabled:opacity-60 flex items-center gap-1"
              >
                <Download className="h-3 w-3" />
                {pwa.saveState === 'saving' ? 'Saving…' : 'Save for offline'}
              </button>
            )}
          </div>
        </section>

        {/* Service outline */}
        <section className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid="portal-outline">
          <div className="mb-2 text-[10px] uppercase tracking-widest text-slate-400">Service Order</div>
          <ol className="text-sm space-y-1">
            {outline.map((b, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <span className="w-6 text-slate-500 text-xs">{i + 1}.</span>
                <span className="text-slate-100">{b.title}</span>
                {b.scripture_ref && (
                  <span className="text-[11px] text-amber-300 ml-2">
                    <BookOpen className="mr-0.5 inline-block h-3 w-3" /> {b.scripture_ref}
                  </span>
                )}
                {b.song && (
                  <span className="text-[11px] text-violet-300 ml-2">
                    <Music className="mr-0.5 inline-block h-3 w-3" /> {b.song.title}
                    {b.song.key && <span className="text-slate-500 ml-1">key {b.song.key}</span>}
                  </span>
                )}
              </li>
            ))}
            {outline.length === 0 && (
              <li className="text-slate-500 text-xs">No outline yet.</li>
            )}
          </ol>
        </section>

        {/* Song cards */}
        <section className="space-y-4" data-testid="portal-songs">
          <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Music className="h-4 w-4 text-violet-300" /> Songs to practice
          </h2>
          {songBlocks.length === 0 && (
            <p className="text-sm text-slate-500">No songs scheduled for this service.</p>
          )}
          {songBlocks.map((b, i) => (
            <SongCard key={`${b.song.id}-${i}`} block={b} />
          ))}
        </section>

        <footer className="text-center text-[10px] text-slate-500 py-6">
          Published from the Faith Works Worship module ·
          {service.blocks?.some((b) => b.song?.ccli_number) && (
            <span> songs licensed via CCLI</span>
          )}
        </footer>
      </div>

      {showIosHelp && (
        <IosInstallHelp onClose={() => setShowIosHelp(false)} />
      )}
    </div>
  );
}


function SongCard({ block }) {
  const s = block.song || {};
  const youTubeId = extractYouTubeId(s.video_url);
  const copyLyrics = () => {
    try { navigator.clipboard.writeText(s.lyrics || ''); toast.success('Lyrics copied'); }
    catch { toast.error('Copy failed'); }
  };
  return (
    <article data-testid={`portal-song-${s.id}`}
             className="rounded-lg border border-white/10 bg-slate-900/60 overflow-hidden shadow-lg">
      <div className="px-4 py-3 flex items-baseline justify-between gap-2 border-b border-white/10">
        <div>
          <h3 className="text-base font-bold text-slate-100">{s.title}</h3>
          <div className="text-[11px] text-slate-400 mt-0.5">
            {s.author || '—'}
            {s.key && <span className="ml-2">key {s.key}</span>}
            {s.ccli_number && <span className="ml-2 opacity-60">CCLI #{s.ccli_number}</span>}
          </div>
        </div>
        <button type="button" onClick={copyLyrics} data-testid={`portal-copy-${s.id}`}
                className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-[10px] text-slate-300 hover:border-slate-500">
          <Copy className="mr-1 inline-block h-3 w-3" /> Copy lyrics
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3">
        {s.lyrics && (
          <pre className="col-span-2 whitespace-pre-wrap px-4 py-3 text-sm text-slate-200 font-serif leading-relaxed">
            {s.lyrics}
          </pre>
        )}
        <div className="p-3 md:border-l border-white/10 space-y-2">
          {youTubeId ? (
            <div className="aspect-video overflow-hidden rounded border border-white/10">
              <iframe
                title={s.title}
                src={`https://www.youtube.com/embed/${youTubeId}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                allowFullScreen
                className="h-full w-full"
                data-testid={`portal-video-${s.id}`}
              />
            </div>
          ) : s.video_url ? (
            <a href={s.video_url} target="_blank" rel="noreferrer"
               data-testid={`portal-video-link-${s.id}`}
               className="flex items-center justify-center gap-1 rounded border border-rose-500/40 bg-rose-500/10 py-2 text-[11px] text-rose-200 hover:bg-rose-500/20">
              <Play className="h-3 w-3" /> Watch video
            </a>
          ) : (
            <p className="text-[11px] text-slate-500">No video attached.</p>
          )}
        </div>
      </div>
    </article>
  );
}


function IosInstallHelp({ onClose }) {
  return (
    <div
      role="dialog"
      data-testid="portal-ios-help"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-slate-900 border border-white/10 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-violet-300">Install on iPhone</div>
            <h3 className="text-base font-semibold text-slate-100">Add to Home Screen</h3>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="portal-ios-help-close"
                  className="text-slate-400 hover:text-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <ol className="text-sm text-slate-200 space-y-2 list-decimal pl-5">
          <li>Tap the <span className="font-semibold">Share</span> icon at the bottom of Safari.</li>
          <li>Scroll and choose <span className="font-semibold">Add to Home Screen</span>.</li>
          <li>Tap <span className="font-semibold">Add</span> — it appears with your church icon.</li>
        </ol>
        <p className="text-[11px] text-slate-500 mt-3">
          Once installed, open the app and tap <span className="text-slate-300">Save for offline</span>
          {' '}so it works on the drive.
        </p>
      </div>
    </div>
  );
}


function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  return m ? m[1] : null;
}
